
/* System defined core library */
import { Component, HostListener, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined enums/constants */
import { ComponentType, Fee } from '../../../shared/enums/scheduling.enum';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';

/* User defined library/components */
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { TimerService } from '../../../shared/services/timer.service';


@Component({
  selector: 'app-multiday-testcenter-reschedule',
  templateUrl: './reschedule-multiday-session.component.html'
})
export class RescheduleMultidaySessionComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }


  /* User defined private/public variables  */
  public selectedAppointments: AppointmentDetail[] = [];
  public region: any;
  public client: Client;
  public program: Program;
  public candidate: Candidate;
  public canNavigateNext: boolean = false;
  public holds: Hold[];
  public booking: Booking;

  /* Service subscriptions */
  selectedAppointments$: Subscription;
  hold$: Subscription;
  region$: Subscription;
  selectedSite$: Subscription;
  client$: Subscription;
  program$: Subscription;
  translateLanguage$: Subscription;
  candidate$: Subscription;
  holds$: Subscription;
  booking$: Subscription;

  constructor(
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.program$ = this.selectedExamDetailService.program.subscribe(program => {
      this.program = program;
    });

    this.client$ = this.selectedExamDetailService.client.subscribe(client => {
      this.client = client;
    });

    this.candidate$ = this.selectedExamDetailService.candidate.subscribe(candidate => {
      this.candidate = candidate;
    });

    this.holds$ = this.selectedExamDetailService.holds.subscribe(holds => {
      this.holds = holds;
    });

    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });
  }

  ngOnInit() {
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.RescheduleMultidaySessionComponent)) return;

    this.selectedAppointments.forEach(
      appt => {
        appt.exam.hasPayment = true;
      });
    let selectedSite = this.selectedAppointments[0].exam.sessions[0].site;

    let siteSlot: SiteSlot = new SiteSlot(
      selectedSite.id,
      selectedSite.href,
      selectedSite.type,
      selectedSite.prefix,
      selectedSite.location,
      []
    );
    this.selectedExamDetailService.setSiteSlot(siteSlot);
    this.selectedExamDetailService.setPrevSites([siteSlot]);
  }

  setSelectedSessions(sessions: Session[]) {
    sessions.forEach((s, x) => {
      if (s.selected) {
        this.selectedAppointments[0].exam.sessions[x].slot = s.slot;
        this.selectedAppointments[0].exam.sessions[x].hold = s.hold;
      }
    });
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  goToPrevPage() {
    this.clearDataOnGoingBack();
    this.routingService.backwardNavigation(ComponentType.RescheduleMultidaySessionComponent);
  }

  clearDataOnGoingBack() {
    this.selectedExamDetailService.setSkipSize(0);
    this.selectedExamDetailService.setSiteSlots(null);
    this.selectedExamDetailService.setSessionGroups([]);
    this.selectedExamDetailService.setSessionAvailabilities([]);
    this.selectedExamDetailService.setSiteSlot(null);
    this.selectedExamDetailService.setHolds([]);
    this.selectedExamDetailService.setSessions([]);
    this.selectedAppointments[0].exam.sessions.forEach((s) => { s.slot = null });
    this.selectedExamDetailService.SetAppointmentDetail(this.selectedAppointments);

    this.timerService.clearClock();
  }

  canNavigateFwd($event: boolean) {
    this.canNavigateNext = $event;
  }

  goToNextPage() {
    if (!this.holds || (this.holds && !this.holds.length)) {
      this.createHolds();
    } else if (this.holds.length) {
      this.moveToNextPage();
    }
    this.getPaymentDetails();
  }

  /* Create Holds for all session slots */
  createHolds() {
    let slots = this.selectedAppointments[0].exam.sessions.filter(s => s.selected).map(s => s.slot);

    this.schedulingApiService.holdMultipleSlots(slots).subscribe((response) => {
      if (response && response.length) {

        this.selectedAppointments[0].exam.sessions.forEach((session, index) => {
          if (session.selected) {
            session.hold.id = response[slots.findIndex(s => s.id === session.slot.id)].id;
          }

          // set the hold object and can be removed after the S/R flow does not look at hold object and looks at holds objects instead
          if (session.selected) this.selectedExamDetailService.setHold(session.hold);
        });
        const holds = this.selectedAppointments[0].exam.sessions.filter(s => s.selected).map(s => s.hold);
        this.selectedExamDetailService.setHolds(holds);
        this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

        this.moveToNextPage();
      }
    })
  }

  moveToNextPage() {
    const selectedSite = this.selectedAppointments[0].exam.sessions[0].site;
    // retrieving consent for an exam
    this.orchestrationApiService.getFlows(
      this.client.code,
      this.program.code,
      this.selectedAppointments[0].exam.code,
      selectedSite.location.address.country
    ).subscribe((resp) => {
      this.setSettingsFromFlows(resp);
      this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleMultidayTestCenterComponent);
    });
  }

  getPaymentDetails(): void {
    let totalAmount: number = 0;
    let currencyCode: string = '';
    const isCnatt = this.booking.tests.some(t => t.cnatt);
    // NO B2B, only single exam

    const selectedSessions = this.selectedAppointments[0].exam.sessions.filter(s => s.selected);

    this.selectedAppointments.forEach(
      (appointmentDetail) => {
        appointmentDetail.payments = [];
        this.schedulingApiService.getBookingPaymentFeeDetail(selectedSessions[0].appointmentId, appointmentDetail.exam.id).subscribe((paymentFee) => {
          paymentFee.forEach(payment => {
            if (payment.type === Fee.PartialRescheduleFee) {
              const tmpPayment: Payment = new Payment(
                isCnatt ? 0 : payment.amount,
                payment.currency,
                Fee.RescheduleFee
              );
              totalAmount += tmpPayment.amount;
              currencyCode = payment.currency;
              appointmentDetail.payments.push(tmpPayment);
              this.selectedExamDetailService.rescheduleFee = {
                totalAmount: totalAmount,
                currency: currencyCode
              };

              this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;

              this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

              if (!appointmentDetail.exam.hasPayment) {
                this.clearPaymentDetails(appointmentDetail.exam.id);
              }
            }
          });
        });
      });
  }

  clearPaymentDetails(examId?: any) {
    examId = examId || this.selectedAppointments[0].exam.id;
    let totalAmount = 0;
    this.selectedAppointments.forEach((appointmentDetail) => {
      if (appointmentDetail.exam.id === examId) {
        appointmentDetail.payments.forEach((payment) => {
          if (payment) {
            payment.amount = 0;
          } else {
              appointmentDetail.payments.push(new Payment(0));
          }
        });
      }
    });

    this.selectedAppointments.forEach(appt => totalAmount += appt.payments.length ? appt.payments[0].amount : 0);
    this.selectedExamDetailService.rescheduleFee['totalAmount'] = totalAmount;

    this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  setSettingsFromFlows(response: any, examId?: string) {
    const notificationResp = response.filter(function (el: any) {
      return el.name === "Verification";
    });

    this.selectedExamDetailService.showTextReminder = notificationResp[0].data &&
      notificationResp[0].data.length &&
      notificationResp[0].data[0].parameters.smsText === "True" ? true : false;

    const paymentResp = response.filter(function (el: any) {
      return el.name == "Payment";
    });

    examId = examId || this.selectedAppointments[0].exam.id;

    this.selectedAppointments.forEach(appt => {
      if (appt.exam.id === examId) {
        appt.exam.hasPayment = paymentResp.length && paymentResp[0].settings.reschedule === 'True';
      }
    });

    const biometricResp = response.filter(function (el: any) {
      return el.name === 'Biometric Consent';
    });
    this.selectedExamDetailService.isBiometricConsentRequiredReschedule =
      !this.selectedExamDetailService.isBiometricConsentSavedInSchedule && (
        biometricResp[0].settings &&
        biometricResp[0].settings.consent === 'True'
      );

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

    this.selectedAppointments.forEach(appt => {
      if (!appt.exam.hasPayment) {
        this.clearPaymentDetails(appt.exam.id);
      }
    });
  }
}